import { useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePasskey as usePasskeyCeremony } from '@htmniseko/htm-passkey-client';
import {passkeyAuthenticateOptions,
  passkeyAuthenticateVerify,
  passkeyList,
  passkeyRegisterOptions,
  passkeyRegisterVerify,
  passkeyRevoke} from 'state/modules/auth';

/**
 * Owner Portal binding for the shared @htmniseko/htm-passkey-client hook.
 *
 * The shared hook owns the WebAuthn browser ceremony (navigator.credentials
 * via @simplewebauthn/browser); this wrapper supplies a PasskeyClient that
 * routes each ceremony step through the portal's Redux actions (which hit the
 * gateway). Verify steps resolve to { verified: true } on a successful
 * dispatch - the resulting session/redirect is handled in auth middleware,
 * not here - and reject (surfacing as the hook's `error`) on failure.
 */
const usePasskey = () => {
  const dispatch = useDispatch();
  const tenantId = useSelector(state => state.tenant.id);
  // owner-api includes a freshly generated recovery-code batch in the
  // register/verify response exactly once - on the account's first passkey.
  // Stashed via a ref (not state): a caller reading this right after
  // `await register(...)` resolves needs the value set DURING that same
  // call - a state setter's update wouldn't be visible in the closure
  // already resuming from that await, only in a future render.
  const recoveryCodesRef = useRef(null);

  const client = useMemo(() => ({
    getAuthenticationOptions: () => dispatch(passkeyAuthenticateOptions(tenantId)),
    verifyAuthentication: async credential => {
      await dispatch(passkeyAuthenticateVerify(credential, tenantId));
      return { verified: true };
    },
    getRegistrationOptions: deviceName => dispatch(passkeyRegisterOptions(deviceName, tenantId)),
    verifyRegistration: async (credential, deviceName) => {
      const passkey = await dispatch(passkeyRegisterVerify(credential, deviceName, tenantId));
      // OWN-232 passkey review: only trust recoveryCodes when it's actually
      // an array of strings - if any future proxy/response-shape change
      // ever reshapes this body unexpectedly, silently returning null here
      // (rather than an unexpected value) fails safe instead of surfacing
      // whatever the malformed field happened to be.
      const recoveryCodes = passkey?.recoveryCodes;
      const hasValidRecoveryCodes = Array.isArray(recoveryCodes) && recoveryCodes.every(code => typeof code === 'string');
      recoveryCodesRef.current = hasValidRecoveryCodes ? recoveryCodes : null;
      return { verified: true, credential: passkey };
    },
    listCredentials: () => dispatch(passkeyList()),
    removeCredential: id => dispatch(passkeyRevoke(id, tenantId)),
  }), [dispatch, tenantId]);

  const ceremony = usePasskeyCeremony(client);

  // Wraps the shared hook's register() (which only resolves to a boolean) so
  // callers get the recovery-code batch directly on the resolved value,
  // rather than reading a separately-updated piece of hook state afterward.
  const register = useCallback(async deviceName => {
    recoveryCodesRef.current = null;
    const added = await ceremony.register(deviceName);
    return { added, recoveryCodes: recoveryCodesRef.current };
  }, [ceremony]);

  return { ...ceremony, register };
};

export default usePasskey;
